/**
 * Strings for roles and permissions (docs/design/roles-permissions.md): the
 * permission list with descriptions, the role editor (display, icon,
 * permissions, members), channel permissions (overwrites, category sync),
 * "View server as role" and the voice order note. Merged last, so the
 * permission labels here replace older ones.
 */
import type { Dict } from './core';

export const rolesEn: Dict = {
  // ---- groups (Discord's)
  'ns.perm.g.general': 'General server permissions',
  'ns.perm.g.generalChannel': 'General channel permissions',
  'ns.perm.g.membership': 'Membership permissions',
  'ns.perm.g.text': 'Text channel permissions',
  'ns.perm.g.voice': 'Voice channel permissions',
  'ns.perm.g.advanced': 'Advanced permissions',
  // ---- labels
  'ns.perm.VIEW_CHANNEL': 'View channels',
  'ns.perm.MANAGE_CHANNELS': 'Manage channels',
  'ns.perm.MANAGE_ROLES': 'Manage roles',
  'ns.perm.MANAGE_GUILD': 'Manage server',
  'ns.perm.VIEW_AUDIT_LOG': 'View audit log',
  'ns.perm.CREATE_INSTANT_INVITE': 'Create invite',
  'ns.perm.CHANGE_NICKNAME': 'Change nickname',
  'ns.perm.MANAGE_NICKNAMES': 'Manage nicknames',
  'ns.perm.KICK_MEMBERS': 'Kick members',
  'ns.perm.BAN_MEMBERS': 'Ban members',
  'ns.perm.MODERATE_MEMBERS': 'Time out members',
  'ns.perm.POKE': 'Poke members',
  'ns.perm.SEND_MESSAGES': 'Send messages',
  'ns.perm.ATTACH_FILES': 'Attach images',
  'ns.perm.ADD_REACTIONS': 'Add reactions',
  'ns.perm.MENTION_EVERYONE': 'Mention @everyone, @here and all roles',
  'ns.perm.MANAGE_MESSAGES': 'Manage messages',
  'ns.perm.PIN_MESSAGES': 'Pin messages',
  'ns.perm.READ_MESSAGE_HISTORY': 'Read message history',
  'ns.perm.CONNECT': 'Connect',
  'ns.perm.SPEAK': 'Speak',
  'ns.perm.MUTE_MEMBERS': 'Mute members',
  'ns.perm.DEAFEN_MEMBERS': 'Deafen members',
  'ns.perm.MOVE_MEMBERS': 'Move members',
  'ns.perm.BYPASS_CHANNEL_PASSWORD': 'Join without the channel password',
  'ns.perm.ADMINISTRATOR': 'Administrator',
  // in a channel, Manage Roles is Manage Permissions (Discord)
  'ns.perm.MANAGE_ROLES.ch': 'Manage permissions',
  // ---- descriptions
  'ns.permd.VIEW_CHANNEL':
    'Lets members see channels by default. Turn it off to make channels private.',
  'ns.permd.MANAGE_CHANNELS':
    'Create, edit and delete channels, and set voice-channel passwords and user limits. They also join password-protected channels without the password.',
  'ns.permd.MANAGE_ROLES':
    'Create roles, and edit, delete and give out roles below their own highest role.',
  'ns.permd.MANAGE_ROLES.ch':
    "Change this channel's permissions, for roles and members below their own highest role.",
  'ns.permd.MANAGE_GUILD': 'See and revoke every invite to the server, not only their own.',
  'ns.permd.VIEW_AUDIT_LOG': 'See the log of who changed what in the server.',
  'ns.permd.CREATE_INSTANT_INVITE': 'Create invite links to bring new people in.',
  'ns.permd.CHANGE_NICKNAME': 'Change their own nickname in this server.',
  'ns.permd.MANAGE_NICKNAMES': "Change other members' nicknames (members below them).",
  'ns.permd.KICK_MEMBERS': 'Remove members from the server. They can come back with an invite.',
  'ns.permd.BAN_MEMBERS': 'Ban members from the server, for good or for a set time.',
  'ns.permd.MODERATE_MEMBERS':
    "Time members out: they can read, but can't write, react or speak until it ends.",
  'ns.permd.POKE': 'Send someone a poke: a small pop-up with a short message.',
  'ns.permd.SEND_MESSAGES': 'Write messages in text channels and in voice-channel chats.',
  'ns.permd.ATTACH_FILES': 'Add images to their messages.',
  'ns.permd.ADD_REACTIONS': 'React to messages with emoji.',
  'ns.permd.MENTION_EVERYONE':
    'Use @everyone and @here, and mention any role, even roles that are not mentionable.',
  'ns.permd.MANAGE_MESSAGES': "Delete other people's messages.",
  'ns.permd.PIN_MESSAGES': 'Pin and unpin messages.',
  'ns.permd.READ_MESSAGE_HISTORY':
    "Read the messages already in a channel. Without it they don't see older messages.",
  'ns.permd.CONNECT': 'Join voice channels.',
  'ns.permd.SPEAK': 'Talk in voice channels. Without it they can only listen.',
  'ns.permd.MUTE_MEMBERS': 'Server-mute people in voice: nobody hears them until it is lifted.',
  'ns.permd.DEAFEN_MEMBERS':
    "Server-deafen people in voice: they can't speak or hear anyone until it is lifted.",
  'ns.permd.MOVE_MEMBERS':
    'Move people between voice channels, disconnect them, and join voice channels that are full.',
  'ns.permd.BYPASS_CHANNEL_PASSWORD':
    'Join password-protected voice channels without typing the password.',
  'ns.permd.ADMINISTRATOR':
    'Every permission in every channel; channel restrictions do not apply to them. It still does not let them change roles at or above their own.',

  // ---- the role list
  'roles.search': 'Search roles',
  'roles.searchNone': 'No role matches.',
  'roles.voiceOrder':
    'People in voice channels are listed by their highest role, in this order, then by name.',
  // ---- the role editor
  'roles.tab.display': 'Display',
  'roles.tab.perms': 'Permissions',
  'roles.tab.members': 'Members ({n})',
  'roles.duplicate': 'Duplicate',
  'roles.copyName': '{name} (copy)',
  'roles.duplicated': 'Role duplicated',
  'roles.duplicatedNoIcon':
    'Role duplicated without its image icon: image uploads are turned off right now',
  'roles.viewAs': 'View server as this role',
  'roles.unsaved': 'You have unsaved changes.',
  'roles.reset': 'Reset',
  'roles.saveFirst': 'Save or reset your changes first.',
  'ns.admin.changedElsewhere':
    'Someone else changed this while you were editing. Their change is shown; your unsaved edits are kept on top of it.',
  // ---- the role icon
  'roles.icon': 'Role icon',
  'roles.icon.help':
    'Shown for people whose highest role with an icon is this one: left of their picture in voice channels, after their name in the member list and in messages.',
  'roles.icon.upload': 'Upload image',
  'roles.icon.emoji': 'Pick emoji',
  'roles.icon.remove': 'Remove icon',
  'roles.icon.none': 'No icon',
  'roles.icon.preview': 'Preview',
  'roles.icon.ready': 'Icon ready. Save to keep it.',
  'roles.icon.err.not-image': "That file isn't an image.",
  'roles.icon.err.decode': "That image couldn't be read. Try a PNG, JPEG or WebP.",
  'roles.icon.err.too-big': 'That image is still too big after shrinking. Try a simpler one.',
  // the console shows every role's icon (display.role_icons = all)
  'roles.icon.help.all':
    'Shown with the icons of the holder’s other roles, top role first: left of their picture in voice channels, after their name in the member list and in messages. Past three icons, the rest are counted in a “+N” chip that names them.',
  // the "+N" chip after three icons: its accessible name
  'roles.icons.more': { one: '{n} more role: {names}', other: '{n} more roles: {names}' },
  'roles.emoji.title': 'Pick an emoji for the role',
  'roles.emoji.custom': 'Or type or paste one emoji',
  'roles.emoji.use': 'Use it',
  'roles.emoji.bad': 'That is not a single emoji.',
  // ---- permissions
  'roles.perm.search': 'Search permissions',
  'roles.perm.clear': 'Clear permissions',
  'roles.perm.none': 'No permission matches.',
  'roles.perm.adminWarn':
    'Administrator gives every permission in every channel. Give it only to people you trust completely.',
  'roles.perm.notYours': "You don't have this permission, so you can't give or take it.",
  // ---- members
  'roles.members.add': 'Add members',
  'roles.members.search': 'Search members',
  'roles.members.none': 'Nobody has this role yet.',
  'roles.members.everyone': 'Everyone in the server has @everyone.',
  'roles.members.remove': 'Remove {name} from this role',
  'roles.members.pickTitle': 'Add members to {role}',
  'roles.members.pickNone': 'Everyone who matches already has this role.',
  'roles.members.addN': 'Add {n}',
  'roles.members.added': 'Added to {role}',
  'roles.members.removed': 'Removed from {role}',
  'roles.members.cant': "You can't give or take this role: it is at or above your highest role.",
  'roles.viewAs.bar':
    "You're viewing the server as {roles}. Only what you see changes; your own permissions don't.",
  'roles.viewAs.back': 'Back to roles',
  'roles.viewAs.exit': 'Stop viewing',

  // ---- channel permissions (overwrites)
  'ow.title': 'Permissions in this channel',
  'ow.synced': 'Synced with the category {category}.',
  'ow.notSynced': 'Not synced with the category {category}.',
  'ow.sync': 'Sync now',
  'ow.syncDone': 'Synced with {category}',
  'ow.breaksSync': 'Changing these stops this channel being synced with {category}.',
  'ow.categoryNote': 'Channels synced with this category change with it.',
  'ow.addRole': 'Add a role…',
  'ow.addMember': 'Add a member…',
  'ow.add': 'Add',
  'ow.remove': 'Remove these permissions',
  'ow.deletedRole': 'Deleted role',
  'ow.leftMember': 'Former member',
  'ow.locked': "It's at or above your highest role, so you can't change it.",
  'ow.managePermsWarn':
    "Manage permissions here lets them change this channel's permissions, their own included.",
  'ow.none': 'No permissions set here yet: everyone follows their roles.',
  'ow.unsaved': 'Save the channel to keep these permission changes.',
  'hub.vc.noConnect': "Your roles can't connect to this channel.",
  'ow.gone':
    "This role was deleted (or this member left). Its permissions stay here until you remove them; they can't be edited.",
  'ow.adminOnly': 'Only the owner and Administrators can allow Manage permissions in a channel.',
  'ow.cantSync': 'You need Manage channels in this channel to sync it.',
  'ns.admin.chNoManage':
    "You don't have Manage channels in this channel, so you can't change its settings or delete it.",
  'ns.admin.chNoManageVoice':
    "Your roles can't connect to this channel, so you can't change its settings or delete it either.",
  'ns.admin.catNoManage':
    "You don't have Manage channels in this category. Pick another category to make the channel in.",
};
